import React, { useMemo } from 'react';

/**
 * Reveals a sentence word by word on first paint — each word fades up out of a
 * soft blur, a beat behind the one before it.
 *
 * Splitting is deliberately by WORD, never by letter. Arabic is a connected
 * script: wrapping each letter in its own element breaks the joins and renders
 * the sentence as disconnected stumps. Word boundaries are safe in every
 * language the site ships.
 *
 * Chinese has no spaces, so a whitespace split returns the whole sentence as
 * one token. That case falls back to a per-character split, which is the
 * natural unit there and keeps the effect from degrading to a single fade.
 *
 * @param {string} text  the sentence
 * @param {number} delay seconds before the first word starts
 * @param {number} step  seconds between consecutive words
 */
const RevealText = ({ text, className = '', delay = 0.16, step = 0.038 }) => {
    const { tokens, spaced } = useMemo(() => {
        const raw = String(text ?? '').trim();
        if (!raw) return { tokens: [], spaced: true };

        const words = raw.split(/\s+/);
        // One token but plenty of characters => a script that does not use
        // spaces. Reveal per character instead, and do not re-insert spaces.
        if (words.length === 1 && Array.from(raw).length > 6) {
            return { tokens: Array.from(raw), spaced: false };
        }
        return { tokens: words, spaced: true };
    }, [text]);

    if (!tokens.length) return null;

    return (
        <span className={className}>
            {tokens.map((token, i) => (
                <React.Fragment key={`${token}-${i}`}>
                    <span
                        className="reveal-word"
                        style={{ '--i': i, '--reveal-base': `${delay}s`, '--reveal-step': `${step}s` }}
                    >
                        {token}
                    </span>
                    {/* A real text node, so the line still wraps normally. */}
                    {spaced && i < tokens.length - 1 ? ' ' : null}
                </React.Fragment>
            ))}
        </span>
    );
};

export default RevealText;
